import toast from 'react-hot-toast'
import { createRef, FC, FormEvent, useState } from 'react'
import { AtSymbolIcon, EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import { HomeIcon } from '@heroicons/react/20/solid'
import { Link } from 'react-router-dom'
import { ROUTES, useMailman } from '../../utils'
import { setUser, useAppDispatch } from '../../redux'
import { Spinner } from '../../components'
import { StyledRegisterView } from '.'

type Props = {
  formData: {
    username: string
    email: string
    password: string
  }
  onChange: (key: string, value: string) => void
}

const RegisterView: FC<Props> = ({ formData, onChange }) => {
  const { mailman } = useMailman()
  const emailInputRef = createRef<HTMLInputElement>()
  const passwordInputRef = createRef<HTMLInputElement>()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e?: FormEvent) => {
    try {
      setLoading(true)
      e?.preventDefault()

      const response = await mailman('auth/register', 'POST', {
        ...formData
      })

      dispatch(setUser(response))
    }
    catch (err) {
      toast.error((err as any).message || 'Whoops, something went wrong.')
    }
    finally {
      setLoading(false)
    }
  }

  const handleUsername = (value: string) => {
    const formattedValue = value.trim()
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '')

    onChange('username', formattedValue)
  }

  return (
    <StyledRegisterView className="auth-layout">
      <div className="auth-form">
        <h1>Let's get you started</h1>
        <form onSubmit={(e) => handleSubmit(e)}>
          <label className="input-container">
            <span className="input-label">Username</span>
            <div className="input">
              <AtSymbolIcon/>
              <input
                type="email"
                placeholder="floppa123"
                value={formData.username}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    emailInputRef.current?.focus()
                    return false
                  }
                }}
                onChange={(e) => handleUsername(e.target.value)}
                enterKeyHint="next"
                required
              />
            </div>
          </label>
          <label className="input-container">
            <span className="input-label">Email Address</span>
            <div className="input">
              <EnvelopeIcon/>
              <input
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    passwordInputRef.current?.focus()
                    return false
                  }
                }}
                onChange={(e) => onChange('email', e.target.value)}
                enterKeyHint="next"
                ref={emailInputRef}
                required
              />
            </div>
          </label>
          <label className="input-container">
            <span className="input-label">Password</span>
            <div className="input">
              <LockClosedIcon/>
              <input
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => onChange('password', e.target.value)}
                enterKeyHint="go"
                ref={passwordInputRef}
                required
              />
            </div>
          </label>
        </form>
        <button
          disabled={!formData.email.trim() || !formData.password.trim() || loading}
          className="submit-btn"
          onClick={() => handleSubmit()}
        >
          {!loading && (
            <>
              <HomeIcon/>
              Open Your Home
            </>
          )}
          {loading && <Spinner/>}
        </button>
        <span className="auth-prompt">
          Already have an account? <Link to={ROUTES.auth.login}>Log in</Link>.
        </span>
      </div>
    </StyledRegisterView>
  )
}

export default RegisterView
